import React, {
  Fragment,
  useState,
  useEffect,
  useReducer,
  useCallback,
} from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useSearch } from "../searchServices";
import { useDebounce } from "../../util/hooks";
import { notBlank } from "../../util/ext";
import { groupChildrenByParent, setIds } from "./organizeUtil";
import { toUpper, equals, mergeRight, pluck } from "ramda";

import { useAddToModule } from "../modules/moduleServices";
import {
  // useAddToAnthology,
  useUpdateAnthologyItems,
} from "../anthologies/anthologyServices";
import { useAddToCollection } from "../collections/collectionServices";
// import { useDeleteCollectionParent } from "../collections/collectionServices";
import { useDeleteModuleItems } from "../modules/moduleServices";
import { useUpdateCollectionItems } from "../collections/collectionServices";

import Container from "@material-ui/core/Container";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import SearchIcon from "@material-ui/icons/Search";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import InputAdornment from "@material-ui/core/InputAdornment";

import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import AddOrganizeMediaToParentMenuItem from "./AddOrganizeMediaToParentMenuItem";

const useStyles = makeStyles(theme => ({
  search: {
    backgroundColor: theme.palette.primary.light,
    borderRadius: "4px",
    position: "relative",
    width: "95%",
    display: "flex",
    alignItems: "center",
    margin: "auto",
  },

  searchIcon: {
    color: theme.palette.primary.darker,
  },

  inputRoot: {
    color: theme.palette.primary.darker,
    flexGrow: 1,
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: `2px solid ${theme.palette.primary.brandPrimary}`,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: `2px solid ${theme.palette.primary.brandPrimary}`,
    },
  },

  inputFocused: {
    borderColor: theme.palette.primary.brandPrimary,
  },

  outline: {
    borderColor: "transparent",
  },

  inputInput: {
    width: "100%",
    padding: theme.spacing(2.5),
  },

  mediaContainer: {
    height: "275px",
    overflowY: "auto",
    marginTop: theme.spacing(2.5),
  },

  menuItem: {
    height: 55,
    "&:hover": {
      backgroundColor: "transparent",
      color: theme.palette.primary.brandPrimary,
    },
  },

  footer: {
    padding: theme.spacing(1.5, 2, 0, 2),
  },

  selectedCount: {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightBold,
  },

  moreButtonContainer: {
    "&:hover": {
      backgroundColor: "transparent",
    },
  },

  moreButton: {
    width: "100%",
  },
}));

const initialSearchState = {
  orderBy: "auditUpdated",
  orderDirection: "desc",
  limit: 20,
  titleOnly: false,
  page: 0,
  noFacets: true,
};

const AddOrganizeMediaToParentMenu = props => {
  const classes = useStyles();
  const {
    open,
    // closeMenu,
    childList,
    // showAlert,
    parentType,
    handleAddSuccess,
  } = props;

  const [parentList, setParentList] = useState([]);
  // const [selectedParents, setSelectedParents] = useState([]);
  const [loading, setLoading] = useState(true);

  const addToModule = useAddToModule();
  const deleteModuleItems = useDeleteModuleItems();

  const updateCollectionItems = useUpdateCollectionItems();
  // const deleteCollectionParent = useDeleteCollectionParent();
  const addToCollection = useAddToCollection();

  const updateAnthologyItems = useUpdateAnthologyItems();
  // const addToAnthology = useAddToAnthology();

  const [state, dispatch] = useReducer((state, action) => {
    switch (action.type) {
      case "UPDATE_PAGE":
        return { ...state, page: action.page };
      case "UPDATE_QUERY":
        return {
          ...state,
          appliedQuery: [action.query],
          page: initialSearchState.page,
        };
      default:
        return state;
    }
  }, initialSearchState);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const onSearchChange = useCallback(event => {
    setSearchTerm(event.target.value);
  }, []);

  useEffect(() => {
    setParentList([]);
    dispatch({ type: "UPDATE_QUERY", query: debouncedSearchTerm });
  }, [debouncedSearchTerm]);

  const searchResults = useSearch(
    mergeRight(state, {
      appliedFacets: [
        {
          name: "type",
          queryParam: "_type",
          terms: [
            {
              code: `${toUpper(parentType)}`,
            },
          ],
        },
      ],
    }),
  );

  useEffect(() => {
    if (searchResults.isSuccess) {
      setParentList(current =>
        equals(state.page, 0)
          ? searchResults.data.media
          : current.concat(searchResults.data.media),
      );
    }
  }, [state, searchResults.data, searchResults.isSuccess]);

  //-- Reset parent list on close
  useEffect(() => {
    if (!open) {
      setParentList([]);
    }
  }, [open]);

  //-- Add Media to Module Handler
  const handleModuleClick = useCallback(
    async module => {
      setLoading(true);
      try {
        const prevParents = groupChildrenByParent(childList);

        // Remove children from previous parents
        for (const { prevParent, newChildren } of prevParents) {
          if (notBlank(prevParent.id)) {
            const ids = setIds({
              mediaType: prevParent.mediaType,
              prevMediaList: prevParent.prevMediaList,
              itemsToRemove: newChildren.ids,
            });

            const body = { id: prevParent.id, ids: ids };

            if (equals(prevParent.mediaType, "collection")) {
              await updateCollectionItems.mutateAsync({
                resourceIds: { id: body.id },
                data: { ids: body.ids },
              });
            } else if (equals(prevParent.mediaType, "anthology")) {
              await updateAnthologyItems.mutateAsync({
                resourceIds: { id: prevParent.id },
                data: { ids: ids },
              });
            } else {
              await deleteModuleItems.mutateAsync(body);
            }
          }
        }

        await addToModule.mutateAsync({
          resourceIds: { id: module.id },
          data: { ids: pluck("id", childList) },
        });

        // showAlert("success", ["Item Successfully Added"]);
        handleAddSuccess();
      } catch (err) {
        // showAlert("error", [err.message]);
      } finally {
        setLoading(false);
      }
    },
    [
      addToModule,
      childList,
      // showAlert,
      updateAnthologyItems,
      deleteModuleItems,
      updateCollectionItems,
      handleAddSuccess,
      // closeMenu,
    ],
  );

  //-- Add Subcollection to Collection Handler
  // const handleAnthologyClick = useCallback(async () => {
  //   setLoading(true);
  //   try {
  //     const collectionsWithParents = filter(
  //       collection => notBlank(collection.parentAnthologyId),
  //       childList,
  //     );

  //     const collections = collectionsWithParents.map(async collection => {
  //       const updatedCollection = await deleteCollectionParent.mutateAsync({
  //         parentId: collection.parentAnthologyId,
  //         childId: collection.id,
  //       });
  //       return updatedCollection;
  //     });

  //     await Promise.all(collections);

  //     const collectionMediaIds = childList.map(collection => collection.id);
  //     await addToAnthology.mutateAsync({
  //       id: selectedParents[0],
  //       contentMediaIds: collectionMediaIds,
  //     });
  //     // showAlert("success", ["Content Successfully Added"]);
  //     handleAddSuccess();
  //   } catch (err) {
  //     // showAlert("error", [err.message]);
  //   } finally {
  //     closeMenu();
  //     setLoading(false);
  //   }
  // }, [
  //   deleteCollectionParent,
  //   childList,
  //   selectedParents,
  //   addToAnthology,
  //   // showAlert,
  //   handleAddSuccess,
  //   closeMenu,
  // ]);

  //-- Handle Moving Items to Collection
  const handleCollectionClick = useCallback(
    async parent => {
      try {
        setLoading(true);

        const prevParents = groupChildrenByParent(childList);

        for (const { prevParent, newChildren } of prevParents) {
          if (notBlank(prevParent.id)) {
            const ids = setIds({
              mediaType: prevParent.mediaType,
              prevMediaList: prevParent.prevMediaList,
              itemsToRemove: newChildren.ids,
            });

            const body = { id: prevParent.id, ids: ids };

            if (equals(prevParent.mediaType, "collection")) {
              await updateCollectionItems.mutateAsync(body);
            } else {
              await deleteModuleItems.mutateAsync(body);
            }
          }
        }

        await addToCollection.mutateAsync({
          resourceIds: { id: parent.id },
          data: { ids: pluck("id", childList) },
        });

        // showAlert("success", ["Content Successfully Added"]);
        handleAddSuccess();
      } catch (err) {
        // showAlert("error", [err.message]);
      } finally {
        setLoading(false);
      }
    },
    [
      childList,
      addToCollection,
      setLoading,
      handleAddSuccess,
      deleteModuleItems,
      updateCollectionItems,
    ],
  );

  const handleNewParentClick = useCallback(
    parent => {
      switch (parent.mediaType) {
        case "module":
          return handleModuleClick(parent);
        // case "anthology":
        //   return handleAnthologyClick(parent);
        case "collection":
          return handleCollectionClick(parent);
        default:
          return;
      }
    },
    [
      // childList,
      // parentType,
      handleModuleClick,
      // handleAnthologyClick,
      handleCollectionClick,
    ],
  );

  //-- Handle loading styling

  useEffect(() => {
    if (searchResults.isSuccess) {
      setLoading(false);
    }
  }, [searchResults.isSuccess]);

  return (
    <Container
      disableGutters
      style={{ height: searchResults.isSuccess ? "100%" : "300px" }}
    >
      <div className={classes.search}>
        <OutlinedInput
          autoFocus
          placeholder="Search…"
          classes={{
            root: classes.inputRoot,
            input: classes.inputInput,
            notchedOutline: classes.outline,
            focused: classes.inputFocused,
          }}
          onChange={onSearchChange}
          inputProps={{ "aria-label": "search" }}
          value={searchTerm}
          startAdornment={
            <InputAdornment>
              <SearchIcon
                fontSize="large"
                classes={{ root: classes.searchIcon }}
              />
            </InputAdornment>
          }
        />
      </div>
      {!loading ? (
        <Fragment>
          <Container disableGutters classes={{ root: classes.mediaContainer }}>
            {parentList.map(parent => (
              <AddOrganizeMediaToParentMenuItem
                key={parent.id}
                parent={parent}
                // onClick={event => handleRowClick(event, parent.id)}
                onClick={() => handleNewParentClick(parent)}
              />
            ))}
            <Fragment>
              {searchResults.isSuccess ? (
                searchResults.data.hasMore && (
                  <Fragment>
                    <MenuItem classes={{ root: classes.moreButtonContainer }}>
                      <Button
                        variant="text"
                        onClick={() =>
                          dispatch({
                            type: "UPDATE_PAGE",
                            page: state.page + 1,
                          })
                        }
                        classes={{ root: classes.moreButton }}
                      >
                        More...
                      </Button>
                    </MenuItem>
                  </Fragment>
                )
              ) : (
                <div>
                  <PanelLoadingIndicator />
                </div>
              )}
            </Fragment>
          </Container>
          {/* <Grid
            container
            justify="space-between"
            alignItems="center"
            className={classes.footer}
          >
            <Typography variant="h5" className={classes.selectedCount}>
              {selectedParents.length} selected
            </Typography>
            <Button
              disabled={isBlank(selectedParents)}
              startIcon={<AddIcon />}
              onClick={handleNewParentClick}
            >
              Add
            </Button>
          </Grid> */}
        </Fragment>
      ) : (
        <PanelLoadingIndicator />
      )}
    </Container>
  );
};

export default AddOrganizeMediaToParentMenu;
